import type { PartnerStructuredColumnDto } from '@artic/api-client';

import type { PartnerContainer } from '../products/types';
import type { ProjectStatus, WorkOrderRow } from './types';

/**
 * Minimal row shape needed by the editability predicates. Both the AG Grid
 * `WorkOrderRow` and the API `ProjectWorkOrderDetail` carry these two fields,
 * so the helpers can be called from either source without an unsafe cast.
 */
export type EditabilityRow = Pick<WorkOrderRow, 'work_order_id' | 'work_order_current_status'>;

/**
 * Which kind of column is being edited.
 *
 * Lifecycle terms (ENG-523 spec → code value):
 *   Drafts                  → 'draft'
 *   Scheduled               → 'scheduled'
 *   Scheduled + Redrafting  → 'redrafting'
 *   In Process              → 'in_progress'
 *   In Process + Redrafting → 'in_progress' (no separate status; rules identical)
 *   Complete                → 'complete'
 *
 * Field categories:
 *   - 'work_order_name' — the human-readable label. Locked from Scheduled
 *     onwards (including during Redrafting): QR codes, printed labels and
 *     floor displays reference it directly.
 *   - 'product_type'    — the load-bearing DFMA Container. Locked from
 *     Scheduled onwards (including during Redrafting): changing it would
 *     change the Bill of Operations and the manufacturing route. If it must
 *     change, the work order is archived and rebuilt in Drafts.
 *   - 'factory'         — the Factory picker (ENG-743). Editable in DRAFT
 *     only: the factory column locks at first release and never unlocks —
 *     from then on the ROUTE drives the factory (Set Route on Scheduled
 *     rows). Redrafting rows keep it read-only.
 *   - 'system'          — every other DFMA Container (dropdowns built by
 *     `buildContainerColumns`).
 *   - 'custom'          — user-defined custom (metadata) columns.
 *
 * Standard Columns are gated separately by `canEditStructuredCell` so each
 * column can carry its own `editable_in_statuses` policy from the registry.
 */
export type ColumnKind = 'work_order_name' | 'product_type' | 'factory' | 'system' | 'custom';

/**
 * Per-row editability predicate for Work Order Name, DFMA Containers
 * (including Product Type), and Custom Columns. `work_order_current_status`
 * encodes both lifecycle and execution state, so the project-level
 * `project_status` doesn't need to contribute extra cases.
 *
 *   - Drafts          → all kinds editable
 *   - Scheduled       → custom only (DFMA, name and PT locked)
 *   - Scheduled+Redrafting → name + product_type locked; other DFMA + custom editable
 *   - In Process      → custom only
 *   - Complete        → custom only (matrix in ENG-523)
 *   - Archived        → nothing editable
 *
 * For Standard Columns (the work_orders DB columns surfaced via the
 * registry), use `canEditStructuredCell` with the column's
 * `editable_in_statuses` policy.
 */
export function canEditCell(
  row: EditabilityRow | undefined,
  kind: ColumnKind,
  _projectStatus: ProjectStatus,
): boolean {
  if (!row) return true;
  const status = row.work_order_current_status;

  // Work Order Name, Product Type and Factory are only editable in Drafts.
  // All are load-bearing once a work order is committed: the name is
  // referenced by printed labels and QR codes; Product Type determines the
  // Bill of Operations; the Factory locks at first release (ENG-743 — after
  // that the route drives it, changed only via Set Route).
  if (kind === 'work_order_name' || kind === 'product_type' || kind === 'factory') {
    return status === 'draft';
  }

  switch (status) {
    case 'draft':
    case 'redrafting':
      return true;
    case 'scheduled':
    case 'in_progress':
    case 'complete':
      return kind === 'custom';
    case 'archived':
      return false;
    default:
      return false;
  }
}

/**
 * Editability predicate for Standard Columns. The registry assigns each
 * column an `editable_in_statuses` list (e.g. Manufacturing Sequence Number is
 * read-only — reordered via the Production Planner; Install Date is editable
 * through `complete`). This helper looks up the row's current status against
 * that list.
 */
export function canEditStructuredCell(
  row: EditabilityRow | undefined,
  editableInStatuses: readonly WorkOrderRow['work_order_current_status'][],
): boolean {
  if (!row) return true;
  return editableInStatuses.includes(row.work_order_current_status);
}

/**
 * "Legacy Products" are those that have not been configured with the new
 * DFMA containers / container options style config — i.e. zero rows in
 * `product_container_selections`. They run on hand-curated Bills of
 * Operations that the materialiser explicitly preserves
 * (`BoOMaterialiserService.syncProduct` skips them with reason
 * `'no-selections'`). Adding a container selection here would silently
 * promote the product to "containerised" and the next Run Sheet release would
 * replace the hand-curated junction with rules-driven materialisation.
 *
 * The grid uses this predicate alongside `canEditCell` / `isApplicable`
 * to lock every container column on a legacy work order's row, regardless
 * of project or work-order status.
 */
export function isLegacyWorkOrder(
  row: EditabilityRow | undefined,
  legacyWorkOrderIds: ReadonlySet<string>,
): boolean {
  if (!row) return false;
  return legacyWorkOrderIds.has(row.work_order_id);
}

/**
 * Bundle of catalogues that `canEditField` needs to resolve a field name to
 * the right editability rule (container vs. structured-column vs. metadata vs.
 * pinned scalar) without re-walking the column-builder logic.
 */
export interface EditabilityContext {
  containers: readonly PartnerContainer[];
  structuredColumns: readonly PartnerStructuredColumnDto[];
  legacyWorkOrderIds: ReadonlySet<string>;
  projectStatus: ProjectStatus;
}

/**
 * Whether a given column field is editable on a given target row, combining
 * the per-row lifecycle rules (`canEditCell` / `canEditStructuredCell`) with
 * the column's category (container vs. structured vs. metadata vs. pinned
 * scalar) and the legacy-product gating.
 *
 * Used by the project-planning sync layer to filter out read-only writes when
 * fan-out operations (drag-fill, "Copy cell(s) down", "Copy product down")
 * span rows whose status doesn't allow the edit. Without this, the backend
 * rejects the whole batch on the first locked cell and the user's intent —
 * applying the value to the editable rows — is lost.
 */
export function canEditField(
  field: string,
  row: EditabilityRow | undefined,
  context: EditabilityContext,
): boolean {
  // Spacer / framework rows: mirror the other predicates' default-true so we
  // don't surprise callers that pass undefined.
  if (!row) return true;

  // Pinned-left Factory column: draft-only (ENG-743 — locks at first release
  // and never unlocks; the route drives the factory from then on). The
  // fill/copy-down engines route through here, so non-draft rows are skipped
  // by fills for free.
  if (field === 'factory_id') {
    return canEditCell(row, 'factory', context.projectStatus);
  }

  // Work Order Name is editable in `draft` only (load-bearing for QR codes
  // and printed labels). Not currently in the copy-down range, but defensive
  // against future callers.
  if (field === 'work_order_name') {
    return canEditCell(row, 'work_order_name', context.projectStatus);
  }

  // Direct product bind — emitted only by a Product / Product Code import that
  // names a product with no container selections. Rebinding the whole product
  // is at least as load-bearing as Product Type, so it follows the same
  // draft-only rule. The legacy gate deliberately does NOT apply: binding a
  // legacy work order to a legacy product is the case this exists for.
  if (field === 'product_id') {
    return canEditCell(row, 'product_type', context.projectStatus);
  }

  // Custom Columns are addressed as `metadata.<key>` and follow the 'custom'
  // rules (editable through `complete`, locked on `archived`).
  if (field.startsWith('metadata.')) {
    return canEditCell(row, 'custom', context.projectStatus);
  }

  // DFMA Container option_code column.
  const container = context.containers.find((c) => c.container_key === field);
  if (container) {
    const kind: ColumnKind = container.container_key === 'product_type' ? 'product_type' : 'system';
    return (
      canEditCell(row, kind, context.projectStatus) &&
      !isLegacyWorkOrder(row, context.legacyWorkOrderIds)
    );
  }

  // Standard Columns from the partner registry (Manufacturing Sequence Number, Phase, the
  // ENG-520 unit-bearing columns, etc.). Per-column policy lives on the DTO.
  // For number_with_unit columns both the value field (work_order_field) and
  // the companion unit field (work_order_unit_field) map back to the same
  // registry entry — drag-fill emits paired writes and the per-field
  // editability check must accept both halves, otherwise the unit write is
  // silently dropped and ensureNumberWithUnitPairs falls back to the target
  // row's cached unit (which is null for rows where the user is filling the
  // column for the first time, causing the whole write to be dropped).
  const structured = context.structuredColumns.find(
    (c) => c.work_order_field === field || c.work_order_unit_field === field,
  );
  if (structured) {
    return canEditStructuredCell(row, structured.editable_in_statuses);
  }

  // Unknown field — be conservative and reject. The grid's `editableColumnIds`
  // gate should already filter to known fields, so landing here means a
  // misconfigured caller and we'd rather drop the write than land a payload
  // the backend rejects.
  return false;
}
